import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Navigate } from 'react-router-dom';
import { showSuccessToast, handleAPIError } from '@/utils/toasts';
import { normalizePhoneNumber } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { APIRequestError } from '@/services/api';
import { getUserFacingError } from '@/utils/errorMessages';
import { analytics, AnalyticsEvents } from '@/services/analytics';
import { useSubscriptionProducts } from '@/contexts/SubscriptionProductsContext';
import { parseOptionalParam, parsePlanParams, savePendingCheckout } from '@/services/checkoutChain';
import type { SubscriptionType } from '@/models';
import { useDarkMode } from '@/hooks/useDarkMode';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import DarkModeToggle from '@/components/common/DarkModeToggle';
import StepIndicator from '@/components/auth/registration/StepIndicator';
import StepAccountDetails from '@/components/auth/registration/StepAccountDetails';
import StepVerificationCode from '@/components/auth/registration/StepVerificationCode';
import StepCheckout from '@/components/auth/registration/StepCheckout';

interface RegistrationProps {
  onRegister: (token: string) => void;
}

export default function Registration({ onRegister }: RegistrationProps) {
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useDarkMode();
  const { products, loading: productsLoading } = useSubscriptionProducts();

  // Ein oder mehrere Pakete aus dem Link (?plan=A&plan=B) — ungültige werden verworfen
  const selectedPlans = parsePlanParams(searchParams, products);

  // Optional coupon code from URL parameter (empty string treated as unset)
  const selectedCoupon = parseOptionalParam(searchParams, 'coupon') ?? null;

  // Optional referrer code from URL parameter (empty string treated as unset)
  const selectedReferrer = parseOptionalParam(searchParams, 'referrer') ?? null;

  // Der Plan lässt sich erst prüfen, wenn die Produkte da sind
  if (productsLoading) {
    return <LoadingSpinner message="Einen Moment..." />;
  }

  // Redirect to landing page if no valid plan selected
  if (selectedPlans.length === 0) {
    return <Navigate to="/" replace />;
  }

  return <RegistrationWizard selectedPlans={selectedPlans} selectedCoupon={selectedCoupon} selectedReferrer={selectedReferrer} onRegister={onRegister} isDarkMode={isDarkMode} />;
}

interface RegistrationWizardProps {
  selectedPlans: SubscriptionType[];
  selectedCoupon: string | null;
  selectedReferrer: string | null;
  onRegister: (token: string) => void;
  isDarkMode: boolean;
}

function RegistrationWizard({ selectedPlans, selectedCoupon, selectedReferrer, onRegister, isDarkMode }: RegistrationWizardProps) {
  const [step, setStep] = useState<2 | 3 | 4>(2);

  // Step 2: Account details
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [acceptedDatenschutz, setAcceptedDatenschutz] = useState(false);
  const [acceptedAGB, setAcceptedAGB] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);

  // Step 3: Verification
  const [registerSecret, setRegisterSecret] = useState('');
  const [resendCooldown, setResendCooldown] = useState(false);
  // Dev/local only: verification code returned by the backend (null in production) so testers can register with fake emails
  const [devEmailCode, setDevEmailCode] = useState<string | null>(null);

  // Shared state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [showLoginLink, setShowLoginLink] = useState(false);

  const cooldownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stabiler Schlüssel für Effekt-Dependencies (das Array bekommt pro Render eine neue Identität)
  const plansKey = selectedPlans.join(',');
  // `selectedPlan` (erstes Paket) bleibt für bestehende Auswertungen erhalten, `selectedPlans` kommt hinzu
  const analyticsPlan = { selectedPlan: selectedPlans[0], selectedPlans: selectedPlans };

  // Persist selected plans (and optional coupon/referrer) in sessionStorage for App.tsx checkout redirect
  useEffect(() => {
    const plans = plansKey.split(',');
    savePendingCheckout({
      plans: plans,
      coupon: selectedCoupon ?? undefined,
      referrerCode: selectedReferrer ?? undefined,
    });
    analytics.track(AnalyticsEvents.REGISTRATION_STARTED, { selectedPlan: plans[0], selectedPlans: plans });
  }, [plansKey, selectedCoupon, selectedReferrer]);

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current !== null) {
        clearTimeout(cooldownTimerRef.current);
      }
    };
  }, []);

  // Step 2 -> Step 3: Request verification token
  const handleRequestToken = async () => {
    setError('');
    setTokenLoading(true);
    setShowLoginLink(false);

    try {
      const response = await APIRoutes.registerToken({ email: email.trim().toLowerCase() });
      setDevEmailCode(response.emailCode);
      analytics.track(AnalyticsEvents.REGISTRATION_ACCOUNT_SUBMITTED, { ...analyticsPlan, success: true });
      setStep(3);
    } catch (err) {
      if (err instanceof APIRequestError && err.errorcode === 'MAIL_ALREADY_EXISTS') {
        analytics.track(AnalyticsEvents.REGISTRATION_ACCOUNT_SUBMITTED, { ...analyticsPlan, success: false, errorcode: 'MAIL_ALREADY_EXISTS' });
        setError('Diese E-Mail-Adresse ist bereits registriert.');
        setShowLoginLink(true);
        return;
      }
      const errorMessage = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten';
      const lowerMsg = errorMessage.toLowerCase();
      const isRateLimit = (err instanceof APIRequestError && err.errorcode === 'REGISTER_TOKEN_RATE_LIMIT') || errorMessage.includes('429') || lowerMsg.includes('too many') || lowerMsg.includes('rate') || lowerMsg.includes('already requested recently');
      if (isRateLimit) {
        analytics.track(AnalyticsEvents.REGISTRATION_ACCOUNT_SUBMITTED, { ...analyticsPlan, success: false, errorcode: 'RATE_LIMIT' });
        setError('Ein Code wurde bereits angefordert. Bitte warte einen Moment.');
        // Token was already sent - user can proceed to enter the code
        setStep(3);
      } else {
        const errorcode = err instanceof APIRequestError ? (err.errorcode ?? 'UNKNOWN') : 'UNKNOWN';
        analytics.track(AnalyticsEvents.REGISTRATION_ACCOUNT_SUBMITTED, { ...analyticsPlan, success: false, errorcode });
        setError(getUserFacingError(err, 'Der Bestätigungscode konnte gerade nicht versendet werden. Bitte überprüfe deine E-Mail-Adresse und versuche es in einem Moment erneut.'));
      }
    } finally {
      setTokenLoading(false);
    }
  };

  // Step 3: Resend verification code
  const handleResendCode = async () => {
    if (resendCooldown) return;

    try {
      const response = await APIRoutes.registerToken({ email: email.trim().toLowerCase() });
      setDevEmailCode(response.emailCode);
      analytics.track(AnalyticsEvents.REGISTRATION_RESEND_CODE, { ...analyticsPlan });
      showSuccessToast('Ein neuer Bestätigungscode wurde gesendet.');
      setResendCooldown(true);
      cooldownTimerRef.current = setTimeout(() => {
        setResendCooldown(false);
        cooldownTimerRef.current = null;
      }, 60000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten';
      const lowerMsg = errorMessage.toLowerCase();
      const isRateLimit = (err instanceof APIRequestError && err.errorcode === 'REGISTER_TOKEN_RATE_LIMIT') || errorMessage.includes('429') || lowerMsg.includes('too many') || lowerMsg.includes('rate') || lowerMsg.includes('already requested recently');
      if (isRateLimit) {
        setError('Ein Code wurde bereits angefordert. Bitte warte einen Moment.');
        setResendCooldown(true);
        cooldownTimerRef.current = setTimeout(() => {
          setResendCooldown(false);
          cooldownTimerRef.current = null;
        }, 60000);
      } else {
        setError(getUserFacingError(err, 'Der Bestätigungscode konnte gerade nicht erneut gesendet werden. Bitte warte einen Moment und versuche es dann noch einmal.'));
        handleAPIError(err, 'Fehler beim erneuten Senden des Codes');
      }
    }
  };

  // Step 3 -> Step 4: Complete registration
  const handleRegister = async () => {
    setError('');
    setLoading(true);
    setShowLoginLink(false);

    try {
      const response = await APIRoutes.register({
        email: email.trim().toLowerCase(),
        password: password.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: normalizePhoneNumber(phone),
        registerSecret: registerSecret,
      });

      analytics.track(AnalyticsEvents.REGISTRATION_CODE_SUBMITTED, { ...analyticsPlan, success: true });
      analytics.track(AnalyticsEvents.REGISTRATION_COMPLETED, { ...analyticsPlan });
      setStep(4);
      showSuccessToast('Registrierung erfolgreich! Willkommen bei Börsencampus');
      onRegister(response.token);
    } catch (err) {
      if (err instanceof APIRequestError && err.errorcode === 'MAIL_ALREADY_EXISTS') {
        analytics.track(AnalyticsEvents.REGISTRATION_CODE_SUBMITTED, { ...analyticsPlan, success: false, errorcode: 'MAIL_ALREADY_EXISTS' });
        setError('Diese E-Mail-Adresse ist bereits registriert.');
        setShowLoginLink(true);
        return;
      }
      const errorcode = err instanceof APIRequestError ? (err.errorcode ?? 'UNKNOWN') : 'UNKNOWN';
      analytics.track(AnalyticsEvents.REGISTRATION_CODE_SUBMITTED, { ...analyticsPlan, success: false, errorcode });
      setError(getUserFacingError(err, 'Die Registrierung hat leider nicht geklappt. Bitte überprüfe deine Angaben und versuche es erneut.', {
        TOKEN_INVALID: 'Der eingegebene Bestätigungscode ist ungültig oder abgelaufen. Bitte überprüfe deine Eingabe oder fordere über „Code erneut senden“ einen neuen Code an.',
      }));
    } finally {
      setLoading(false);
    }
  };

  // Step 3 -> Step 2: Go back
  const handleBackToAccountDetails = () => {
    setStep(2);
    setRegisterSecret('');
    setError('');
    setShowLoginLink(false);
  };

  const stepTitle = step === 2
    ? 'Dein Account'
    : step === 3
      ? 'E-Mail bestätigen'
      : 'Checkout';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-4 py-8 relative">
      <DarkModeToggle className="absolute top-4 right-4" />

      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 space-y-6">
          {/* Logo */}
          <div className="text-center mb-2">
            <img
              src={isDarkMode ? "/logo_light.png" : "/logo.png"}
              alt="Börsencampus Logo"
              className="h-16 object-contain mx-auto my-4"
            />
            <div className="w-20 h-1 bg-blue-600 mx-auto rounded-full mb-6"></div>
          </div>

          {/* Step indicator */}
          <StepIndicator currentStep={step} />

          {/* Step title */}
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
            {stepTitle}
          </h2>

          {/* Step content */}
          {step === 2 && (
            <StepAccountDetails
              selectedPlans={selectedPlans}
              hasCoupon={selectedCoupon !== null}
              email={email}
              firstName={firstName}
              lastName={lastName}
              phone={phone}
              password={password}
              passwordConfirm={passwordConfirm}
              acceptedDatenschutz={acceptedDatenschutz}
              acceptedAGB={acceptedAGB}
              acceptedDisclaimer={acceptedDisclaimer}
              error={error}
              loading={tokenLoading}
              showLoginLink={showLoginLink}
              onEmailChange={setEmail}
              onFirstNameChange={setFirstName}
              onLastNameChange={setLastName}
              onPhoneChange={setPhone}
              onPasswordChange={setPassword}
              onPasswordConfirmChange={setPasswordConfirm}
              onAcceptedDatenschutzChange={setAcceptedDatenschutz}
              onAcceptedAGBChange={setAcceptedAGB}
              onAcceptedDisclaimerChange={setAcceptedDisclaimer}
              onNext={handleRequestToken}
            />
          )}

          {step === 3 && (
            <StepVerificationCode
              email={email}
              registerSecret={registerSecret}
              devEmailCode={devEmailCode}
              error={error}
              loading={loading}
              resendCooldown={resendCooldown}
              showLoginLink={showLoginLink}
              onRegisterSecretChange={setRegisterSecret}
              onSubmit={handleRegister}
              onResendCode={handleResendCode}
              onBack={handleBackToAccountDetails}
            />
          )}

          {step === 4 && (
            <StepCheckout totalSteps={selectedPlans.length} />
          )}

          {/* Footer */}
          <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Powered by SvenBolz Trading Academy
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
